import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useNegocio } from './AliadoDashboardLayout';
import { BookOpen, QrCode, ExternalLink, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function AliadoCartasPage() {
    const negocio = useNegocio();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/me/oknegocio/products'),
            api.get('/me/oknegocio/product-categories'),
        ]).then(([p, c]) => {
            setProducts(p.data);
            setCategories(c.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const menuUrl = negocio ? `${window.location.origin}/aliado/${negocio.slug}#menu` : '';

    const copyLink = () => {
        navigator.clipboard.writeText(menuUrl);
        toast.success('Enlace copiado');
    };

    const productsByCategory = (catId) => products.filter(p => p.category_id === catId && p.available);
    const uncategorized = products.filter(p => !p.category_id && p.available);

    const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-orange-600" /> OkCartas</h1>
                <p className="text-sm text-muted-foreground">Menú digital con código QR para tus clientes.</p>
            </div>

            {/* QR + link panel */}
            <div className="rounded-2xl border bg-card p-5 flex flex-col sm:flex-row items-center gap-5">
                <div className="h-28 w-28 rounded-2xl bg-muted flex items-center justify-center shrink-0">
                    <QrCode className="h-14 w-14 text-muted-foreground" />
                </div>
                <div className="space-y-3 text-center sm:text-left">
                    <div>
                        <div className="font-semibold text-sm">Tu menú digital</div>
                        <div className="text-xs text-muted-foreground mt-0.5 break-all">{menuUrl}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-center sm:justify-start">
                        <Button size="sm" onClick={copyLink} variant="outline" className="h-8 text-xs">
                            Copiar enlace
                        </Button>
                        <Button size="sm" onClick={() => window.open(menuUrl, '_blank')} variant="outline" className="h-8 text-xs">
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver menú
                        </Button>
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Smartphone className="h-3 w-3" /> Escanea el QR para ver tu menú en el celular
                    </div>
                </div>
            </div>

            {/* Menu preview */}
            {loading ? (
                <div className="text-sm text-muted-foreground text-center py-6">Cargando menú…</div>
            ) : products.length === 0 ? (
                <div className="rounded-2xl border bg-card p-8 text-center">
                    <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <div className="text-sm text-muted-foreground">Tu menú está vacío. <a href="/aliado/dashboard/products" className="text-orange-600 hover:underline">Agrega productos</a> y aparecerán aquí.</div>
                </div>
            ) : (
                <div className="space-y-6">
                    <h2 className="font-semibold text-sm">Vista previa del menú</h2>

                    {categories.filter(c => productsByCategory(c.id).length > 0).map(cat => (
                        <div key={cat.id}>
                            <h3 className="font-medium text-sm text-orange-700 mb-2 pb-1 border-b border-orange-100">{cat.name}</h3>
                            <div className="grid gap-2 sm:grid-cols-2">
                                {productsByCategory(cat.id).map(p => (
                                    <div key={p.id} className="rounded-xl border bg-card p-3 flex gap-3 items-center">
                                        {p.image_url
                                            ? <img src={p.image_url} alt={p.name} className="h-12 w-12 rounded-lg object-cover shrink-0 border" />
                                            : <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center text-xl shrink-0">🍽️</div>
                                        }
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{p.name}</div>
                                            {p.description && <div className="text-xs text-muted-foreground truncate">{p.description}</div>}
                                            {p.preparation_time > 0 && <div className="text-[10px] text-muted-foreground">⏱ {p.preparation_time} min</div>}
                                        </div>
                                        <div className="text-sm font-bold text-orange-700 shrink-0">{fmt(p.price)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {uncategorized.length > 0 && (
                        <div>
                            <h3 className="font-medium text-sm text-muted-foreground mb-2 pb-1 border-b">Sin categoría</h3>
                            <div className="grid gap-2 sm:grid-cols-2">
                                {uncategorized.map(p => (
                                    <div key={p.id} className="rounded-xl border bg-card p-3 flex gap-3 items-center">
                                        {p.image_url
                                            ? <img src={p.image_url} alt={p.name} className="h-12 w-12 rounded-lg object-cover shrink-0 border" />
                                            : <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center text-xl shrink-0">📦</div>
                                        }
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{p.name}</div>
                                            {p.description && <div className="text-xs text-muted-foreground truncate">{p.description}</div>}
                                        </div>
                                        <div className="text-sm font-bold text-orange-700 shrink-0">{fmt(p.price)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
